// Fonts are fully self-hosted — there are no external font requests. Inter is
// the app-wide body face (`typography.fontFamily` in mangomap-ui); Manrope is
// the marketing heading face (`headingFontFamily` in landing). Weights are
// limited to those actually used across apps/web and every package it renders
// (incl. `fontWeight: "medium"` → 500 and `"bold"` → 700); no 100/200/300/900
// usage exists in live code.
// NB: use the static @fontsource packages, NOT @fontsource-variable — those
// declare a different family ("Inter Variable"), which would require renaming
// the family in the shared mangomap-ui theme and in apps/partner + ui-workshop.
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/manrope/400.css";
import "@fontsource/manrope/500.css";
import "@fontsource/manrope/600.css";
import "@fontsource/manrope/700.css";
import "@fontsource/manrope/800.css";
import "aos/dist/aos.css";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import "maplibre-gl/dist/maplibre-gl.css";
import "~mangomap-ui/styles/global.css";

import { EmotionCache } from "@emotion/react";
import {
	AnalyticsConsentProvider,
	Cookies,
	useAnalyticsConsent,
} from "app-cookies";
import { AuthProvider } from "~auth/contexts/auth";
import { useGetCurrentPublicAccountInfoByCurrentSlug } from "~auth/queries/use-get-public-account-info-by-slug";
import type { GetPublicAccountInfoBySlugQuery } from "~auth/types/__generated/gql/graphql";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { sharedInMemoryStorage } from "aws-amplify/utils";
import maplibregl from "maplibre-gl";
import { NextPage } from "next";
import { AppProps } from "next/app";
import { useRouter } from "next/router";
import NProgress from "nprogress";
import i18next, { changeLanguage } from "i18next";
import { useEffect, useMemo, type ReactNode } from "react";
import { TagManager } from "~tag-manager/tag-manager";
import { I18nProvider } from "~utils/providers/i18n-provider";
import { QueryProvider } from "~utils/providers/query-provider";
import { ReactDevTools } from "~utils/configs/ReactDevTools";
import { isWhitelabelEnabled } from "~utils/functions/whitelabel-branding";
import {
	defaultSettings,
	SettingsProvider,
} from "~mangomap-ui/components/settings/settings-context";
import {
	CUSTOM_LOCALE,
	type PrimaryColor,
	type Settings,
} from "~mangomap-ui/types/settings";

// Valid V4 palette names accepted by the runtime theme provider's
// settings.primaryColor union. Any other string from brandingConfig (legacy
// values from older migrations, or hex colors) is routed differently in
// deriveBrandedSettings below.
const VALID_PRIMARY_COLORS: ReadonlySet<PrimaryColor> = new Set<PrimaryColor>([
	"neonBlue",
	"green",
	"indigo",
	"purple",
	"red",
	"orange",
	"custom",
	"mango",
]);
import { ThemeProvider } from "~mangomap-ui/components/theme-provider";
import { V4ReleaseMessageController } from "~/components/v4-release-message-controller";
import { LocalizationProvider } from "~mangomap-ui/components/localization-provider";
import GlobalStyles from "@mui/material/GlobalStyles";
import { routes } from "config/paths";
import { Toaster } from "~ui-components/components/atoms/toaster";
import {
	DefaultSeo as DefaultNextSeo,
	DefaultSeoProps,
	OrganizationJsonLd,
} from "next-seo";
import { OpenGraph } from "next-seo/lib/types";
import { ORGANIZATION } from "~landing/seo/constants";
import { WebSiteSchema } from "~landing/seo/website-schema";
import { amplifyConfig } from "~/utils/amplify-config";
import { CustomerGoogleAnalytics } from "~/components/customer-analytics/customer-google-analytics";
import { isCustomerAnalyticsRoute } from "~/components/customer-analytics/customer-analytics-routes";
import { useAccountBoundaryNavigation } from "~/components/customer-analytics/use-account-boundary-navigation";

NProgress.configure({ showSpinner: false });

Amplify.configure(amplifyConfig, { ssr: true });

// SSR enables CookieStorage for ordinary top-level MangoMap pages. A cross-site
// iframe cannot reliably read those SameSite=Lax cookies after Cognito signs in,
// so keep its tokens in the loaded document only. This deliberately requires a
// new embedded sign-in handoff after an iframe document reload.
if (typeof window !== "undefined" && window.self !== window.top) {
	cognitoUserPoolsTokenProvider.setKeyValueStorage(sharedInMemoryStorage);
}

ReactDevTools.init();

export interface CustomAppProps extends AppProps {
	Component: NextPage;
	emotionCache?: EmotionCache;
}

const loadCogProtocol = async () => {
	const { cogProtocol } = await import("@geomatico/maplibre-cog-protocol");
	maplibregl.addProtocol("cog", cogProtocol);
};
if (typeof window !== "undefined") void loadCogProtocol();

// If any route has been moved to Devias theme, remove it from this list
const OLD_ROUTES = ["/franchise"];

const deriveBrandedSettings = (
	publicAccountInfo: GqlResultOf<GetPublicAccountInfoBySlugQuery> | undefined,
): Settings | undefined => {
	const brandingConfig = publicAccountInfo?.brandingConfig;
	if (!brandingConfig) return undefined;

	// brandingConfig.primaryColor is dual-purpose:
	//   - a custom hex (from the brand panel UI, e.g. "#32bcad") → routed via
	//     settings.brand.colors.primary + settings.primaryColor: "custom" →
	//     createCustomPrimaryTone(hex) in color-schemes.js
	//   - a V4 palette name (from v3-migration, e.g. "neonBlue") → routed via
	//     settings.primaryColor → primarySchemes[name] in color-schemes.js
	//   - a legacy palette name not in the V4 type union (e.g. "chateauGreen"
	//     from older migrations) → fall back to "mango" so MUI gets a valid
	//     value; color-schemes.js will log a warning if it doesn't recognize it
	// Disambiguate by the "#" prefix. Without this, palette names crash
	// MUI's lighten() at color-schemes.js:163.
	const primary = brandingConfig.primaryColor ?? undefined;
	const isCustomHex = typeof primary === "string" && primary.startsWith("#");
	const paletteName =
		!isCustomHex && primary && VALID_PRIMARY_COLORS.has(primary as PrimaryColor)
			? (primary as PrimaryColor)
			: undefined;

	return {
		brand: {
			logo: brandingConfig.logoUrl
				? {
						url: brandingConfig.logoUrl,
						fileName: brandingConfig.logoUrl.split("/").pop() ?? "logo",
						size: 0,
					}
				: undefined,
			theme: brandingConfig.presetTheme ?? undefined,
			...(isCustomHex ? { colors: { primary } } : {}),
			mapSettings: {
				highlightColor: brandingConfig.highlightColor ?? undefined,
			},
		},
		primaryColor: isCustomHex ? "custom" : (paletteName ?? "mango"),
	};
};

const App = ({
	Component,
	pageProps,
	// emotionCache = clientSideEmotionCache,
	router,
}: CustomAppProps) => {
	const isOldRoute = OLD_ROUTES.includes(router.pathname);
	const isFranchise = router.pathname === routes.franchise;

	useNprogress(router);

	return (
		<>
			{/* Unset locally, and TagManager declines any production ID off prod. */}
			<TagManager
				gaId={
					isFranchise
						? process.env.FRANCHISE_GA_MEASUREMENT_ID
						: process.env.GIS_GA_MEASUREMENT_ID
				}
				gtmId={
					isFranchise
						? process.env.FRANCHISE_GOOGLE_TAG_MANAGER_ID
						: process.env.GIS_GOOGLE_TAG_MANAGER_ID
				}
			/>
			<QueryProvider>
				<I18nProvider language="en">
					<LocalizationProvider>
						<AuthProvider>
							<V4ReleaseMessageController />
							<AnalyticsConsentProvider>
								<BrandedSeo />
								{isOldRoute ? (
									<Component {...pageProps} />
								) : (
									<AppSettingsProvider>
										<AppGlobalStyles />
										<Cookies />
										<Component {...pageProps} />
										<Toaster position="bottom-center" />
									</AppSettingsProvider>
								)}
							</AnalyticsConsentProvider>
						</AuthProvider>
					</LocalizationProvider>
				</I18nProvider>
			</QueryProvider>
		</>
	);
};

const AppSettingsProvider = ({ children }: { children: ReactNode }) => {
	const router = useRouter();
	const { consent } = useAnalyticsConsent();
	const { data: livePublicAccountInfo } =
		useGetCurrentPublicAccountInfoByCurrentSlug();
	const customerAnalyticsActive = Boolean(
		isCustomerAnalyticsRoute(router.pathname) &&
			(livePublicAccountInfo?.googleAnalyticsEnabled ?? false) &&
			livePublicAccountInfo?.brandingConfig?.ga4MeasurementId,
	);
	useAccountBoundaryNavigation(router, customerAnalyticsActive);

	const initialSettings = useMemo<Settings>(() => {
		const brandedSettings = deriveBrandedSettings(livePublicAccountInfo);
		return brandedSettings
			? { ...defaultSettings, ...brandedSettings }
			: defaultSettings;
	}, [livePublicAccountInfo]);

	const localisationConfig = livePublicAccountInfo?.localisationConfig;
	useEffect(() => {
		if (!localisationConfig) return;

		if (localisationConfig.overridesByLocale) {
			try {
				const parsed = JSON.parse(
					localisationConfig.overridesByLocale,
				) as Record<string, Record<string, string>>;
				const overrides = parsed[CUSTOM_LOCALE] ?? {};
				i18next.addResourceBundle(
					CUSTOM_LOCALE,
					"translation",
					overrides,
					true,
					true,
				);
			} catch (error) {
				console.error("Failed to parse overridesByLocale for i18n sync", error);
			}
		}

		void changeLanguage(
			localisationConfig.localisationEnabled ? CUSTOM_LOCALE : "en",
		);
	}, [localisationConfig]);

	return (
		<SettingsProvider settings={initialSettings}>
			<ThemeProvider>
				<CustomerGoogleAnalytics
					accountSlug={livePublicAccountInfo?.slug}
					consent={consent}
					enabled={customerAnalyticsActive}
					measurementId={
						livePublicAccountInfo?.brandingConfig?.ga4MeasurementId
					}>
					{children}
				</CustomerGoogleAnalytics>
			</ThemeProvider>
		</SettingsProvider>
	);
};

export default App;

const useNprogress = (router) => {
	useEffect(() => {
		const handleRouteStart = () => NProgress.start();
		const handleRouteDone = () => NProgress.done();

		router.events.on("routeChangeStart", handleRouteStart);
		router.events.on("routeChangeComplete", handleRouteDone);
		router.events.on("routeChangeError", handleRouteDone);

		return () => {
			// Make sure to remove the event handler on unmount!
			router.events.off("routeChangeStart", handleRouteStart);
			router.events.off("routeChangeComplete", handleRouteDone);
			router.events.off("routeChangeError", handleRouteDone);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

const AppGlobalStyles = () => (
	<GlobalStyles
		styles={(t) => `
			/* Remove is recharts is not used */
			.recharts-cartesian-axis-tick-value {
				color: var(--mui-palette-text-secondary);
				font-size: var(--fontSize-xs);
			}

			.recharts-cartesian-grid line {
				stroke: var(--mui-palette-divider);
			}

			.recharts-pie path:focus {
				outline: none;
			}

			.popup-content > .maplibregl-popup-content {
				padding: 0px;
			}

			/* Remove if fullcalendar is not used */
			.fc {
				--fc-bg-event-opacity: 1;
				--fc-border-color: var(--mui-palette-divider);
				--fc-daygrid-event-dot-width: 10px;
				--fc-event-bg-color: var(--mui-palette-background-paper);
				--fc-event-border-color: var(--mui-palette-divider);
				--fc-event-text-color: var(--mui-palette-primary-contrastTex);
				--fc-list-event-hover-bg-color: var(--mui-palette-background-default);
				--fc-neutral-bg-color: var(--mui-palette-background-default);
				--fc-page-bg-color: var(--mui-palette-background-default);
				--fc-today-bg-color: var(--mui-palette-background-level1);
				color: var(--mui-palette-text-primary);
			}

			.fc .fc-license-message {
				display: none;
			}

			.fc .fc-list {
				border-color: transparent;
			}

			.fc .fc-scrollgrid {
				border-color: transparent;
			}

			.fc .fc-scrollgrid-section-header > th {
				border-color: transparent;
			}

			.fc .fc-scrollgrid-section-body > td {
				border-color: transparent;
			}

			.fc .fc-col-header-cell:first-of-type {
				border-left-color: transparent;
			}

			.fc .fc-col-header-cell:last-of-type {
				border-right-color: transparent;
			}

			.fc .fc-col-header-cell-cushion {
				font-size: 0.75rem;
				font-weight: 600;
				letter-spacing: 0.5px;
				line-height: 2.5;
				padding: 8px;
				text-transform: uppercase;
			}

			.fc .fc-daygrid-day-frame {
				padding: 12px;
			}

			.fc .fc-day-other .fc-daygrid-day-top {
				color: var(--mui-palette-text-secondary);
			}

			.fc .fc-event {
				background: transparent;
				border: none;
				padding: 0;
			}

			/* Variables */
			:root {
				--fontSize-xs: 0.75rem;
				--fontSize-sm: 0.875rem;
				--fontSize-md: 1rem;
				--fontSize-lg: 1.125rem;
				--fontSize-xl: 1.25rem;
				--fontSize-2xl: 1.5rem;

				--icon-fontSize-sm: 1rem;
				--icon-fontSize-md: 1.25rem;
				--icon-fontSize-lg: 1.5rem;

				/* Remember to keep in sync with theme breakpoints */
				--maxWidth-xs: 0;
				--maxWidth-sm: 600px;
				--maxWidth-md: 900px;
				--maxWidth-lg: 1200px;
				--maxWidth-xl: 1440px;
			}

			*:focus-visible {
				outline: 2px solid var(--mui-palette-primary-main);
			}

			html {
				height: 100%;
			}

			body {
				height: 100%;
			}

			#nprogress {
				pointer-events: none;
			}

			#nprogress .bar {
				background: ${t.palette.primary.main};

				position: fixed;
				z-index: 9999;
				top: 0;
				left: 0;

				width: 100%;
				height: 2px;
			}

			/* Fancy blur effect */
			#nprogress .peg {
				display: block;
				position: absolute;
				right: 0px;
				width: 100px;
				height: 100%;
				box-shadow: 0 0 10px ${t.palette.primary.main}, 0 0 5px ${t.palette.primary.main};
				opacity: 1;

				-webkit-transform: rotate(3deg) translate(0px, -4px);
				-ms-transform: rotate(3deg) translate(0px, -4px);
				transform: rotate(3deg) translate(0px, -4px);
			}

			/* Remove these to get rid of the spinner */
			#nprogress .spinner {
				display: block;
				position: fixed;
				z-index: 1101;
				top: 15px;
				right: 15px;
			}

			#nprogress .spinner-icon {
				width: 18px;
				height: 18px;
				box-sizing: border-box;

				border: solid 2px transparent;
				border-top-color: ${t.palette.primary.main};
				border-left-color: ${t.palette.primary.main};
				border-radius: 50%;

				-webkit-animation: nprogress-spinner 400ms linear infinite;
				animation: nprogress-spinner 400ms linear infinite;
			}

			.nprogress-custom-parent {
				overflow: hidden;
				position: relative;
			}

			.nprogress-custom-parent #nprogress .spinner,
			.nprogress-custom-parent #nprogress .bar {
				position: absolute;
			}

			@-webkit-keyframes nprogress-spinner {
				0% {
					-webkit-transform: rotate(0deg);
				}
				100% {
					-webkit-transform: rotate(360deg);
				}
			}
			@keyframes nprogress-spinner {
				0% {
					transform: rotate(0deg);
				}
				100% {
					transform: rotate(360deg);
				}
			}
		`}
	/>
);

const MANGO_TITLE = "Simple, professional online GIS maps";
const MANGO_DESCRIPTION =
	"The accessible and user-friendly alternative to Esri for smart spenders. Create and securely share maps your way.";
const NEUTRAL_DESCRIPTION = "Create and securely share maps your way.";

// No font preconnects: Inter and Manrope are self-hosted via @fontsource, so
// fonts.googleapis.com / fonts.gstatic.com are never contacted. Preconnecting to
// an origin nothing requests just opens a TCP+TLS connection that is never used.
const commonLinkTags = [
	{ rel: "preconnect", href: "https://dev.visualwebsiteoptimizer.com" },
];

const mangoFaviconTags = [
	{ rel: "icon" as const, href: "/app-assets/icons/favicon.ico" },
	{
		rel: "apple-touch-icon" as const,
		href: "/app-assets/icons/apple-touch-icon.png",
		sizes: "180x180",
	},
	{
		rel: "icon" as const,
		type: "image/png",
		sizes: "32x32",
		href: "/app-assets/icons/favicon-32x32.png",
	},
	{
		rel: "icon" as const,
		type: "image/png",
		sizes: "16x16",
		href: "/app-assets/icons/favicon-16x16.png",
	},
];

const buildSeoProps = ({
	appName,
	title,
	description,
	faviconTags,
	openGraphImages,
}: {
	appName?: string;
	title?: string;
	description?: string;
	faviconTags: typeof mangoFaviconTags;
	openGraphImages?: OpenGraph["images"];
}): DefaultSeoProps => ({
	...(title ? { title } : {}),
	titleTemplate: `%s${appName ? ` - ${appName}` : ""}`,
	...(description ? { description } : {}),
	dangerouslySetAllPagesToNoFollow: process.env.ENABLE_SEO !== "true",
	dangerouslySetAllPagesToNoIndex: process.env.ENABLE_SEO !== "true",
	themeColor: "#ff5722",
	// No site-wide `canonical`/`openGraph.url`: each page owns its own (see
	// `MarketingSeo`). A blanket default made every page self-canonicalise to the
	// homepage. Pages that set neither now emit none, which is correct for
	// account/auth/error routes that should not point at the marketing homepage.
	robotsProps: {
		maxSnippet: -1,
		maxImagePreview: "large",
		maxVideoPreview: -1,
	},
	additionalMetaTags: [
		...(appName
			? [
					{ name: "application-name", content: appName },
					{ name: "apple-mobile-web-app-title", content: appName },
				]
			: []),
		{ name: "format-detection", content: "telephone=no" },
		{ name: "msapplication-TileColor", content: "#FF5621" },
		{ name: "msapplication-tap-highlight", content: "no" },
	],
	additionalLinkTags: [...faviconTags, ...commonLinkTags],
	openGraph: {
		type: "website",
		...(title ? { title } : {}),
		...(description ? { description } : {}),
		...(appName ? { siteName: appName } : {}),
		locale: "en_GB",
		...(openGraphImages ? { images: openGraphImages } : {}),
	},
});

const BrandedSeo = () => {
	const router = useRouter();
	const accountSlug = router.query["account-name"] as string | undefined;
	const isAccountScopedRoute = Boolean(accountSlug);

	const { data: publicAccountInfo, isLoading } =
		useGetCurrentPublicAccountInfoByCurrentSlug();

	const seoProps = useMemo<DefaultSeoProps>(() => {
		if (!isAccountScopedRoute) {
			return buildSeoProps({
				appName: "Mango GIS",
				title: MANGO_TITLE,
				description: MANGO_DESCRIPTION,
				faviconTags: mangoFaviconTags,
				openGraphImages: [
					{
						url: "/app-assets/mangoGIS.png",
						width: 1182,
						height: 670,
						alt: "Mango GIS",
						type: "image/png",
					},
				],
			});
		}

		// While loading, use neutral SEO (no branding in case it's whitelabel)
		if (isLoading && !publicAccountInfo) {
			return buildSeoProps({ faviconTags: [] });
		}

		if (!publicAccountInfo) {
			return buildSeoProps({ faviconTags: [] });
		}

		const whitelabelEnabled = isWhitelabelEnabled(
			publicAccountInfo.brandingConfig,
		);

		// Non-whitelabel: full Mango branding
		if (!whitelabelEnabled) {
			return buildSeoProps({
				appName: "Mango GIS",
				description: MANGO_DESCRIPTION,
				faviconTags: mangoFaviconTags,
				openGraphImages: [
					{
						url: "/app-assets/mangoGIS.png",
						width: 1182,
						height: 670,
						alt: "Mango GIS",
						type: "image/png",
					},
				],
			});
		}

		// Whitelabel: use account branding
		const accountName = publicAccountInfo.companyName;
		const accountLogoUrl =
			publicAccountInfo.brandingConfig?.logoUrl?.trim() ?? undefined;

		return buildSeoProps({
			appName: accountName,
			description: NEUTRAL_DESCRIPTION,
			faviconTags: accountLogoUrl
				? [
						{ rel: "icon", href: accountLogoUrl },
						{
							rel: "apple-touch-icon",
							href: accountLogoUrl,
							sizes: "180x180",
						},
					]
				: [],
			openGraphImages: accountLogoUrl
				? [{ url: accountLogoUrl, alt: accountName, type: "image/png" }]
				: undefined,
		});
	}, [isAccountScopedRoute, isLoading, publicAccountInfo]);

	return (
		<>
			<DefaultNextSeo {...seoProps} />
			{/* Mango-branded marketing routes only — never on account/whitelabel pages */}
			{!isAccountScopedRoute && (
				<>
					<OrganizationJsonLd
						useAppDir={false}
						name={ORGANIZATION.name}
						url={ORGANIZATION.url}
						logo={ORGANIZATION.logo}
						sameAs={ORGANIZATION.sameAs}
					/>
					<WebSiteSchema />
				</>
			)}
		</>
	);
};
